/**
 * Settings → MOU.
 *
 * Whether the MOU stage sits between a won deal and an onboarded client, and
 * what a newly prepared MOU opens with.
 *
 * Super-admin only to change, like Implementation Approvals: turning
 * "block onboarding" off removes the control everyone else is working under,
 * and that must not be something the person blocked by it can do for
 * themselves.
 *
 * The two switches are separate on purpose. Tracking and gating are different
 * asks — a team can want the MOU screen for visibility without a half-signed
 * document stopping implementation.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "antd";
import {
  BellRing, FileSignature, Info, Loader2, Mail, PenLine, RefreshCw, ShieldCheck, UserCheck, X,
} from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { MOU_CONFIG_URL, MOU_TEMPLATES_URL, USERS_SIMPLE_URL } from "../../constants/api";
import MOUTemplateManager from "../mou/MOUTemplateManager";
import MergeFieldEditor from "../mou/MergeFieldEditor";

const Toggle = ({ checked, onChange, disabled, label, hint }) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{hint}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform"
        style={{ transform: checked ? "translateX(1rem)" : "translateX(0)" }}
      />
    </button>
  </div>
);

export default function MOUSettings() {
  const { fetch, update } = useApiMethods();
  const navigate = useNavigate();

  const [cfg, setCfg]         = useState(null);
  const [users, setUsers]     = useState([]);
  // The manager list is held separately from cfg so a removal is reflected the
  // instant it is clicked, rather than after the round trip.
  const [ids, setIds]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [mergeFields, setMergeFields] = useState([]);

  const load = useCallback(() => {
    fetch(MOU_CONFIG_URL, {}, setLoading).then((res) => {
      if (res?.status === "success") {
        setCfg(res.data);
        setIds((res.data.managers || []).map((m) => m.id));
      } else ToastMsg("error", res?.message || "Could not load the configuration");
    });
    fetch(USERS_SIMPLE_URL).then((res) => {
      const list = res?.data || res?.results || [];
      setUsers(list.map((u) => ({
        value: u.id,
        label: [u.login_fname, u.login_lname].filter(Boolean).join(" ").trim()
               || u.login_name || `#${u.id}`,
      })));
    }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(MOU_TEMPLATES_URL).then((res) => setMergeFields(res?.data?.merge_fields || [])).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch = {}) => {
    setSaving(true);
    const body = {
      require_mou: cfg?.require_mou,
      block_onboarding_until_signed: cfg?.block_onboarding_until_signed,
      require_manager_signoff: cfg?.require_manager_signoff,
      default_validity_days: cfg?.default_validity_days,
      default_title: cfg?.default_title,
      default_scope: cfg?.default_scope,
      default_terms: cfg?.default_terms,
      ...patch,
    };
    const res = await update(MOU_CONFIG_URL, body);
    setSaving(false);
    if (res?.status === "success") {
      setCfg(res.data);
      setIds((res.data.managers || []).map((m) => m.id));
      ToastMsg("success", "Saved");
    } else {
      ToastMsg("error", res?.message || "Failed to save");
      // The optimistic removal above may not have landed; resync.
      load();
    }
  };

  const set = (key, val) => setCfg((prev) => ({ ...prev, [key]: val }));
  const readOnly = cfg ? !cfg.can_edit : true;

  if (loading && !cfg) {
    return (
      <ContainerCard>
        <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </ContainerCard>
    );
  }

  return (
    <ContainerCard>
      <div className="max-w-3xl space-y-5 p-1">

        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <FileSignature className="h-4 w-4 text-indigo-500" />
              MOU
            </h2>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              A won deal is not a commitment in writing. Turn this on and a lead has to reach a
              signed MOU before it can be onboarded; the{" "}
              <button className="underline" onClick={() => navigate("/mou")}>MOU screen</button>{" "}
              is where they are chased. Every client already in the system was recorded as signed
              when this was installed, so switching it on does not put your existing book into a
              chase list.
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={load} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {readOnly && (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Only a super admin can change these. You are seeing what is currently in force.
          </div>
        )}

        {cfg?.inherited && (
          <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            This project has no MOU settings of its own and is inheriting the organisation-wide
            ones. Saving here creates settings for this project, and it will stop following the
            default.
          </div>
        )}

        <div className="space-y-3">
          <Toggle
            checked={!!cfg?.require_mou}
            disabled={readOnly || saving}
            onChange={(v) => { set("require_mou", v); save({ require_mou: v }); }}
            label="Require an MOU after a deal is won"
            hint="Shows the MOU stage for this project and puts every Closed Won lead on it."
          />
          <Toggle
            checked={!!cfg?.block_onboarding_until_signed}
            disabled={readOnly || saving || !cfg?.require_mou}
            onChange={(v) => {
              set("block_onboarding_until_signed", v);
              save({ block_onboarding_until_signed: v });
            }}
            label="Block onboarding until the MOU is signed"
            hint="Refuses to create an onboarded client from a lead whose MOU is not signed.
                  Turn this off to track MOUs without stopping anyone. Clients added directly,
                  with no lead behind them, are never blocked — there is no won deal for an MOU
                  to attach to."
          />
          <Toggle
            checked={!!cfg?.require_manager_signoff}
            disabled={readOnly || saving}
            onChange={(v) => {
              set("require_manager_signoff", v);
              save({ require_manager_signoff: v });
            }}
            label="Only an MOU manager may sign one off"
            hint="Restricts recording a signature, declining, cancelling and reopening to the
                  people named below. Anyone with MOU edit rights can still prepare, send and
                  chase. Without this, whoever chases an MOU can also declare it signed — which
                  walks the deal straight past the gate above."
          />
        </div>

        {/* ── who manages MOUs ──────────────────────────────────────── */}
        <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
            <UserCheck className="h-3.5 w-3.5 text-gray-400" /> MOU managers
          </h3>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            These people close an MOU out — record the signature, or record that the client
            declined. They are notified when a client signs or declines through their link, so
            somebody owns the moment a deal becomes ready to onboard.
            {" "}Super admins can always close one out, so an MOU is never stranded by a manager
            leaving.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {ids.length === 0 && (
              <span className="text-xs text-gray-400">
                Nobody is named — only super admins can sign one off.
              </span>
            )}
            {(cfg?.managers || []).map((mgr) => (
              <span
                key={mgr.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1
                           text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200
                           dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30"
              >
                {mgr.name || `User ${mgr.id}`}
                {!readOnly && (
                  <button
                    title="Remove"
                    onClick={() => {
                      const next = ids.filter((i) => i !== mgr.id);
                      setIds(next);
                      save({ manager_ids: next });
                    }}
                  >
                    <X className="h-3 w-3 opacity-60 hover:opacity-100" />
                  </button>
                )}
              </span>
            ))}
          </div>

          {!readOnly && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Select
                showSearch
                allowClear
                className="w-64"
                placeholder="Add an MOU manager"
                optionFilterProp="label"
                options={users.filter((u) => !ids.includes(u.value))}
                value={undefined}
                onChange={(v) => {
                  if (!v) return;
                  const next = [...new Set([...ids, v])];
                  setIds(next);
                  save({ manager_ids: next });
                }}
              />
              {saving && <span className="text-xs text-gray-400">Saving…</span>}
            </div>
          )}

          {/* The order matters: turning the requirement on with nobody named
              leaves only super admins able to close an MOU out. */}
          {cfg?.require_manager_signoff && ids.length === 0 && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800
                          dark:bg-amber-500/10 dark:text-amber-300">
              Sign-off is restricted but no manager is named, so only super admins can record a
              signature right now. Name somebody above.
            </p>
          )}

          {cfg?.i_am_manager && (
            <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400">
              You can sign MOUs off, so client signatures and declines will come to you.
            </p>
          )}
        </section>

        {/* ── signing ───────────────────────────────────────────────── */}
        <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
            <PenLine className="h-3.5 w-3.5 text-gray-400" /> Signing
          </h3>
          <Toggle
            checked={!!cfg?.require_sign_otp}
            disabled={readOnly || saving}
            onChange={(v) => { set("require_sign_otp", v); save({ require_sign_otp: v }); }}
            label="Verify the signer's email with a one-time code"
            hint="Before signing, the client must enter a 6-digit code emailed to the address the MOU
                  was sent to. Proves the person signing controls that mailbox — not just that they
                  hold a forwarded link. MOUs with no recipient email are not affected."
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Company signatory</label>
              <Input value={cfg?.company_signatory_name || ""} disabled={readOnly}
                     placeholder="Who countersigns for your company"
                     onChange={(e) => set("company_signatory_name", e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Designation</label>
              <Input value={cfg?.company_signatory_designation || ""} disabled={readOnly}
                     placeholder="e.g. Director"
                     onChange={(e) => set("company_signatory_designation", e.target.value)} />
            </div>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Printed in the company signature block of every MOU PDF, and prefilled when an MOU
            manager countersigns.
          </p>
          {!readOnly && (
            <div className="flex justify-end">
              <Button size="sm" disabled={saving} onClick={() => save({
                company_signatory_name: cfg?.company_signatory_name || "",
                company_signatory_designation: cfg?.company_signatory_designation || "",
              })}>
                Save signatory
              </Button>
            </div>
          )}
        </section>

        {/* ── automation ────────────────────────────────────────────── */}
        <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
              <BellRing className="h-3.5 w-3.5 text-gray-400" /> Reminders, expiry and renewals
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Runs once a day. Everything here is off until you turn it on.
            </p>
          </div>
          <Toggle
            checked={!!cfg?.reminders_enabled}
            disabled={readOnly || saving}
            onChange={(v) => { set("reminders_enabled", v); save({ reminders_enabled: v }); }}
            label="Email the client a reminder while an MOU is unsigned"
            hint="Sent to the recipient (and CC) with the signing link and PDF, on the days below
                  after it was sent. Stops as soon as it is signed, declined, cancelled or expired."
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Remind on days after sending</label>
              <Input value={cfg?.reminder_days || ""} placeholder="3, 7" disabled={readOnly}
                     onChange={(e) => set("reminder_days", e.target.value)}
                     onBlur={() => !readOnly && save({ reminder_days: cfg?.reminder_days || "" })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Renewal alerts, days before contract end</label>
              <Input value={cfg?.renewal_alert_days || ""} placeholder="30, 15, 7" disabled={readOnly}
                     onChange={(e) => set("renewal_alert_days", e.target.value)}
                     onBlur={() => !readOnly && save({ renewal_alert_days: cfg?.renewal_alert_days || "" })} />
            </div>
          </div>
          <Toggle
            checked={!!cfg?.auto_expire}
            disabled={readOnly || saving}
            onChange={(v) => { set("auto_expire", v); save({ auto_expire: v }); }}
            label="Mark an MOU expired when its 'valid until' date passes"
            hint="Moves it to Expired, revokes the signing link and tells the owner and MOU
                  managers. An expired MOU can be reopened with a fresh date."
          />
          <Toggle
            checked={!!cfg?.renewal_alerts_enabled}
            disabled={readOnly || saving}
            onChange={(v) => { set("renewal_alerts_enabled", v); save({ renewal_alerts_enabled: v }); }}
            label="Alert the owner before a signed MOU's contract ends"
            hint="In-app notification to the owner and MOU managers on the days above, for MOUs
                  with a contract end date. Stops once a renewal has been prepared."
          />
        </section>

        {/* ── the MOU email ─────────────────────────────────────────── */}
        <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
              <Mail className="h-3.5 w-3.5 text-gray-400" /> The MOU email
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              What "Send to client" opens with. The sender can still change it before sending.
              Leave blank to use the built-in wording. Include{" "}
              <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">{"{{sign_link}}"}</code> so the
              client can sign online.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Subject</label>
            <Input value={cfg?.email_subject || ""} disabled={readOnly}
                   placeholder="{{mou_title}} {{mou_number}} — {{client_name}}"
                   onChange={(e) => set("email_subject", e.target.value)} />
          </div>
          <MergeFieldEditor semantic value={cfg?.email_body || ""} fields={mergeFields} readOnly={readOnly}
                            height={220} placeholder="Built-in message is used when this is empty"
                            onChange={(v) => set("email_body", v)} />
          {!readOnly && (
            <div className="flex justify-end">
              <Button size="sm" disabled={saving} onClick={() => save({
                email_subject: cfg?.email_subject || "",
                email_body: (cfg?.email_body || "").replace(/<[^>]+>/g, "").trim() ? cfg.email_body : "",
              })}>
                Save email
              </Button>
            </div>
          )}
        </section>

        {/* ── templates ─────────────────────────────────────────────── */}
        <MOUTemplateManager />

        {/* ── what a new MOU opens with ─────────────────────────────── */}
        <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              What a newly prepared MOU opens with
            </p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Prefills only. Anyone preparing an MOU can change them, and editing these never
              touches an MOU that already exists.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs font-medium">Title</label>
              <Input
                value={cfg?.default_title || ""}
                disabled={readOnly}
                onChange={(e) => set("default_title", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Valid for (days)</label>
              <Input
                type="number"
                min={0}
                value={cfg?.default_validity_days ?? 15}
                disabled={readOnly}
                onChange={(e) => set("default_validity_days", e.target.value)}
              />
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                0 = no expiry
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">Default scope</label>
            <Textarea
              rows={3}
              value={cfg?.default_scope || ""}
              disabled={readOnly}
              onChange={(e) => set("default_scope", e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">Default terms</label>
            <Textarea
              rows={6}
              value={cfg?.default_terms || ""}
              disabled={readOnly}
              onChange={(e) => set("default_terms", e.target.value)}
            />
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button size="sm" disabled={saving} onClick={() => save()}>
                {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Save defaults
              </Button>
            </div>
          )}
        </div>
      </div>
    </ContainerCard>
  );
}
